import { useEffect, useMemo, useRef } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import "quill-emoji/dist/quill-emoji.css";
import { colors, formats } from "./ToolbarOptions";
import { preserveWhitespaceForQuill } from "./preserveWhitespaceForQuill";
import styles from "./Editor.module.css";

/**
 * Quill’s built-in “tooltip” is only the link/video URL popup — not hover labels.
 * We add title + data-tooltip (+ aria-label) so toolbar controls are discoverable.
 */
const getToolbarButtonLabel = (button) => {
  const formatClass = Array.from(button.classList).find(
    (c) => c.startsWith("ql-") && c !== "ql-active" && c !== "ql-selected",
  );
  if (!formatClass) return null;

  const format = formatClass.slice(3);
  const value = button.getAttribute("value");

  const simple = {
    bold: "Bold",
    italic: "Italic",
    underline: "Underline",
    strike: "Strikethrough",
    blockquote: "Block quote",
    "code-block": "Code block",
    link: "Insert link",
    image: "Insert image",
    video: "Insert video",
    formula: "Insert formula",
    clean: "Clear formatting",
  };
  if (simple[format]) return simple[format];

  if (format === "header") {
    if (!value || value === "false") return "Normal paragraph";
    return `Heading ${value}`;
  }
  if (format === "list") {
    if (value === "ordered") return "Numbered list";
    if (value === "bullet") return "Bullet list";
    if (value === "check") return "Checklist";
  }
  if (format === "indent") {
    if (value === "-1") return "Decrease indent";
    if (value === "+1") return "Increase indent";
  }
  if (format === "script") {
    if (value === "sub") return "Subscript";
    if (value === "super") return "Superscript";
  }
  if (format === "align") {
    if (!value) return "Align left";
    if (value === "center") return "Align center";
    if (value === "right") return "Align right";
    if (value === "justify") return "Justify";
  }
  if (format === "direction") {
    return value === "rtl" ? "Right to left" : "Left to right";
  }

  return format.replace(/-/g, " ").replace(/^\w/, (c) => c.toUpperCase());
};

const PICKER_LABELS = {
  "ql-header": "Heading level",
  "ql-size": "Font size",
  "ql-font": "Font family",
  "ql-background": "Background / theme color (for gradient text)",
  "ql-color": "Text color",
  "ql-align": "Text alignment",
};

const applyToolbarTooltips = (root) => {
  if (!root) return;
  const toolbar = root.querySelector(".ql-toolbar");
  if (!toolbar) return;

  toolbar.querySelectorAll("button").forEach((button) => {
    const label = getToolbarButtonLabel(button);
    if (!label) return;
    button.setAttribute("title", label);
    button.setAttribute("aria-label", label);
    button.setAttribute("data-tooltip", label);
  });

  toolbar.querySelectorAll(".ql-picker").forEach((picker) => {
    const kind = Array.from(picker.classList).find(
      (c) => c.startsWith("ql-") && c !== "ql-picker" && c !== "ql-expanded",
    );
    const label = (kind && PICKER_LABELS[kind]) || "Options";
    const labelEl = picker.querySelector(".ql-picker-label");
    if (!labelEl) return;
    labelEl.setAttribute("title", label);
    labelEl.setAttribute("aria-label", label);
    labelEl.setAttribute("data-tooltip", label);
  });
};

// Stable module config — recreating this each render remounts the editor.
const EDITOR_MODULES = {
  toolbar: [
    [
      { header: "1" },
      { header: "2" },
      { header: "3" },
      { header: [1, 2, 3, 4, 5, 6, false] },
      { size: [] },
      { font: [] },
    ],
    [{ background: colors }],
    ["bold", "italic", "underline", "strike", "blockquote"],
    [{ align: [] }],
    [{ script: "sub" }, { script: "super" }],
    [
      { list: "ordered" },
      { list: "bullet" },
      { indent: "-1" },
      { indent: "+1" },
    ],
    ["link", "image", "video"],
    ["clean"],
  ],
  clipboard: {
    matchVisual: false,
  },
};

const Editor = (props) => {
  const rootRef = useRef(null);

  // Quill collapses plain multi-spaces on load — protect them as nbsp first.
  // Parent remounts this editor per item/field via `key`, so defaultValue is initial only.
  const initialHtml = useMemo(
    () => preserveWhitespaceForQuill(props.defaultValue || ""),
    [props.defaultValue],
  );

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;

    applyToolbarTooltips(root);

    const frame = window.requestAnimationFrame(() => {
      applyToolbarTooltips(root);
    });

    const observer = new MutationObserver(() => {
      applyToolbarTooltips(root);
    });
    const toolbar = root.querySelector(".ql-toolbar");
    if (toolbar) {
      observer.observe(toolbar, { childList: true, subtree: true });
    }

    return () => {
      window.cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, []);

  return (
    <div className={styles.editorRoot} ref={rootRef}>
      <ReactQuill
        theme="snow"
        onChange={props.onChange}
        defaultValue={initialHtml}
        modules={EDITOR_MODULES}
        formats={formats}
        placeholder={props.placeholder || "Write content…"}
      />
    </div>
  );
};

export default Editor;
